import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Shield, Cookie, Database, Download, Trash2 } from "lucide-react";
import axios from 'axios';
import { showToast } from '@/utils/toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = '/api';

const PrivacySettingsModal = React.memo(({ 
  isOpen, 
  onClose,
  user
}) => {
  const [settings, setSettings] = useState({
    cookie_consent: true,
    analytics_tracking: true,
    marketing_communications: false
  });
  const [saving, setSaving] = useState(false);
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      loadSettings();
    }
  }, [isOpen, user]);

  const loadSettings = async () => {
    try {
      const response = await axios.get(`${API}/privacy/settings/${user.id}`);
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      // Use defaults
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/privacy/settings`, {
        user_id: user.id,
        ...settings
      });
      showToast.success("Gizlilik ayarları kaydedildi");
    } catch (error) {
      showToast.error("Ayarlar kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const handleDataRequest = async (type) => {
    setRequesting(true);
    try {
      if (type === 'download') {
        await axios.post(`${API}/privacy/data-request`, {
          user_id: user.id,
          request_type: 'download'
        });
        showToast.success("Verileriniz hazırlanıyor. E-posta ile gönderilecek.");
      } else if (type === 'delete') {
        await axios.post(`${API}/privacy/data-request`, {
          user_id: user.id,
          request_type: 'delete'
        });
        showToast.success("Silme talebiniz alındı. İşlem 30 gün içinde tamamlanacak.");
      }
    } catch (error) {
      showToast.error("İstek gönderilemedi");
    } finally {
      setRequesting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" aria-label="Gizlilik Ayarları">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Gizlilik Ayarları
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <Card className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Cookie className="h-5 w-5 text-muted-foreground" />
                <div>
                  <Label htmlFor="cookie">Çerez Kullanımı</Label>
                  <p className="text-xs text-muted-foreground">
                    Deneyiminizi iyileştirmek için çerezler kullanılır
                  </p>
                </div>
              </div>
              <Switch
                id="cookie"
                checked={settings.cookie_consent}
                onCheckedChange={(checked) => 
                  setSettings(prev => ({ ...prev, cookie_consent: checked }))
                }
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Database className="h-5 w-5 text-muted-foreground" />
                <div>
                  <Label htmlFor="analytics">Analitik Takibi</Label>
                  <p className="text-xs text-muted-foreground">
                    Uygulamayı geliştirmek için kullanım verileri toplanır
                  </p>
                </div>
              </div>
              <Switch
                id="analytics"
                checked={settings.analytics_tracking}
                onCheckedChange={(checked) => 
                  setSettings(prev => ({ ...prev, analytics_tracking: checked }))
                }
              />
            </div>
            
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="h-5 w-5 text-muted-foreground" />
                <div>
                  <Label htmlFor="marketing">Pazarlama İletişimi</Label>
                  <p className="text-xs text-muted-foreground">
                    Yeni özellikler ve kampanyalar hakkında bilgi alın
                  </p>
                </div>
              </div>
              <Switch
                id="marketing"
                checked={settings.marketing_communications}
                onCheckedChange={(checked) => 
                  setSettings(prev => ({ ...prev, marketing_communications: checked }))
                }
              />
            </div>
          </Card>
          
          <div className="space-y-3">
            <h4 className="font-medium text-sm">Veri Hakları (KVKK/GDPR)</h4>
            <div className="space-y-2">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => handleDataRequest('download')}
                disabled={requesting}
              >
                <Download className="h-4 w-4 mr-2" />
                Verilerimi İndir
              </Button>
              
              <Button 
                variant="outline" 
                className="w-full justify-start text-destructive hover:text-destructive"
                onClick={() => handleDataRequest('delete')}
                disabled={requesting}
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Hesabımı ve Verilerimi Sil
              </Button>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={saving} className="flex-1">
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </Button>
            <Button variant="outline" onClick={onClose}>
              İptal
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
});

PrivacySettingsModal.displayName = 'PrivacySettingsModal';

export default PrivacySettingsModal;
